import { useEffect, useRef, useState, useCallback } from "react";
import WIFI_OFFLINE_DATA_URL from "./offlineWifiData";

/**
 * Session M / iter111 — No Internet Connection screen (visual fix).
 *
 * Owner spec:
 *   • Vertical stack (centered):
 *       1. Wi-Fi + Indian decorative visual (transparent PNG, blends into
 *          beige background — NO visible rectangle / card / border).
 *       2. Exact explanatory sentence (Montserrat, dark brown, centered).
 *       3. "Try to reconnect" red button with active darker-red state.
 *   • The Wi-Fi visual MUST be a local asset (served from /offline-wifi.png)
 *     so the overlay renders even when the device is fully offline.
 *   • Image protection (parity with the rest of the site):
 *       - draggable={false}
 *       - onContextMenu preventDefault (blocks right-click save)
 *       - CSS: -webkit-touch-callout: none, user-select: none
 *         (blocks mobile long-press "Save image" sheet on iOS/Android)
 *       - On a sustained touch (~500ms) we fire a single short
 *         navigator.vibrate(30) — one subtle haptic, no repeats.
 *   • Reconnect probing logic is untouched (fetch /api/healthz with
 *     cache:no-store, unlimited retries, 400ms minimum visual delay).
 */

const WIFI_SRC = WIFI_OFFLINE_DATA_URL;
const HEALTH_URL = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/healthz`;

const SENTENCE =
  "It looks like your device is not connected to the internet. Please check your internet connection and try again";

export default function OfflineGuard() {
  const [offline, setOffline] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false,
  );
  const [probing, setProbing] = useState(false);
  const longPressTimer = useRef(null);
  const didVibrate = useRef(false);

  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const probe = useCallback(async () => {
    setProbing(true);
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const res = await fetch(HEALTH_URL, {
        method: "GET",
        cache: "no-store",
        signal: ctrl.signal,
        headers: { "Cache-Control": "no-cache, no-store, must-revalidate" },
      });
      clearTimeout(t);
      if (res.ok) setOffline(false);
      else setOffline(true);
    } catch {
      setOffline(true);
    } finally {
      setTimeout(() => setProbing(false), 400);
    }
  }, []);

  // Long-press detector on the Wi-Fi image: one short vibration, no menu.
  const onImgTouchStart = useCallback(() => {
    didVibrate.current = false;
    longPressTimer.current = setTimeout(() => {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        try { navigator.vibrate(30); } catch { /* ignore */ }
      }
      didVibrate.current = true;
    }, 500);
  }, []);
  const clearLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  if (!offline) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="No internet connection"
      data-testid="offline-guard"
      className={`fixed inset-0 z-[9999] bg-[#faf2e3] flex items-center justify-center transition-opacity duration-300 ${probing ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      style={{ touchAction: "pan-y" }}
    >
      <div className="w-full max-w-xl mx-auto flex flex-col items-center justify-center px-6 py-10 text-center">
        <img
          src={WIFI_SRC}
          alt=""
          aria-hidden="true"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          onTouchStart={onImgTouchStart}
          onTouchEnd={clearLongPress}
          onTouchCancel={clearLongPress}
          onTouchMove={clearLongPress}
          data-testid="offline-wifi-image"
          className="w-[220px] sm:w-[280px] md:w-[340px] h-auto select-none pointer-events-auto"
          style={{
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
            backgroundColor: "transparent",
          }}
        />
        <p
          data-testid="offline-message"
          className="font-serif text-[#3a1f14] text-base sm:text-lg md:text-xl leading-relaxed mt-8 max-w-md"
          style={{ fontFamily: "'Montserrat', ui-sans-serif, system-ui, sans-serif", fontWeight: 500 }}
        >
          {SENTENCE}
        </p>
        <button
          type="button"
          onClick={probe}
          disabled={probing}
          data-testid="offline-retry-btn"
          className="offline-retry mt-8 inline-flex items-center gap-2 bg-[#9d1a1a] text-white font-semibold px-7 py-3 rounded-full shadow-md transition-colors duration-100 hover:bg-[#881515] active:bg-[#5e0d0d] active:translate-y-[1px] disabled:opacity-80"
          style={{ fontFamily: "'Montserrat', ui-sans-serif, system-ui, sans-serif" }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
            <path d="M3 21v-5h5" />
          </svg>
          Try to reconnect
        </button>
      </div>
    </div>
  );
}
